import {
  Outlet,
  createRootRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import "../index.css";
import { ErrorBoundary } from "../components/error-boundary";
import { PageLoading } from "../components/page-loading";
import { FooterProvider } from "../contexts/footer-context";
import {
  isPayPathname,
  shouldRedirectPayOnPageShow,
} from "../libs/pay-route-guard";

const enableDevtools = false;

const TanStackRouterDevtools = !enableDevtools
  ? () => null // Render nothing in production
  : React.lazy(() =>
      // Lazy load in development
      import("@tanstack/router-devtools").then((res) => ({
        default: res.TanStackRouterDevtools,
        // For Embedded Mode
        // default: res.TanStackRouterDevtoolsPanel
      })),
    );

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const state = useRouterState();
  const navigate = useNavigate();
  const authedRoute = state.matches.find((m) => m.id === "/_authed/");

  useEffect(() => {
    // QR読取後に履歴へ残った過去の /pay/:storeSlug へ戻った場合、
    // 古い店舗に誤決済しないよう常にトップへ戻す。
    const redirectToHomeFromPay = () => {
      if (isPayPathname(window.location.pathname)) {
        navigate({ to: "/", replace: true });
      }
    };

    // Android戻るボタン / iOSスワイプバック対策（同一ドキュメント履歴）。
    const handlePopState = () => {
      // popstate直後はURLだけ先に変わるため、次のタスクで評価する
      setTimeout(redirectToHomeFromPay, 0);
    };

    // WebViewやSafariでBFCache復帰した戻る遷移対策。
    // pageshow時に「履歴復帰」であることを判定し、/payならトップへ置き換える。
    const handlePageShow = (event: PageTransitionEvent) => {
      const firstNavigationEntry = performance.getEntriesByType(
        "navigation",
      )[0] as PerformanceNavigationTiming | undefined;
      const shouldRedirect = shouldRedirectPayOnPageShow({
        pathname: window.location.pathname,
        persisted: event.persisted,
        navigationType: firstNavigationEntry?.type,
      });

      if (shouldRedirect) {
        navigate({ to: "/", replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [navigate]);

  // 初期読み込み時の認証中(_authed の beforeLoad 中)にローディング画面を表示する
  if (
    authedRoute &&
    authedRoute.status === "pending" &&
    authedRoute.cause === "enter"
  ) {
    return <PageLoading />;
  }

  return (
    <>
      <ErrorBoundary>
        <Toaster />
        <FooterProvider>
          <Outlet />
        </FooterProvider>
        <Suspense>
          <TanStackRouterDevtools />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
